import type { MeasuredImage } from 'features/libra/drawing/pixelRectangleValidation'
import type { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import isEqual from 'lodash/isEqual'
import { useCallback, useRef, useState } from 'react'
import { v4 } from 'uuid'

import {
  addInspectionArea,
  inspectionAreaGeometryChanged,
  removeInspectionArea,
  updateInspectionArea,
  validateInspectionAreaGeometryChanges,
} from './planningModel'
import {
  InspectionArea,
  InspectionAreaDrawing,
  InspectionAreaUpdate,
} from './planningTypes'

type InspectionAreaGeometryContext = {
  zones: readonly ZoneParams[]
  measuredImage?: MeasuredImage
}

export const useInspectionAreasDraft = (
  savedAreas: InspectionArea[],
  persist: (areas: InspectionArea[]) => Promise<void>,
  disabled: boolean,
  geometryContext?: InspectionAreaGeometryContext
) => {
  const [draft, setDraft] = useState<InspectionArea[]>()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const areas = draft ?? savedAreas
  const hasChanges = draft !== undefined && !isEqual(draft, savedAreas)
  const currentAreasRef = useRef(areas)
  const geometryContextRef = useRef(geometryContext)
  const inFlightSaveRef = useRef<Promise<void>>()

  currentAreasRef.current = areas
  geometryContextRef.current = geometryContext

  const accept = useCallback(
    (
      proposed: InspectionArea[],
      requireMeasuredImageIds: readonly string[]
    ) => {
      const validationError = validateInspectionAreaGeometryChanges({
        baselineAreas: savedAreas,
        proposedAreas: proposed,
        zones: geometryContextRef.current?.zones ?? [],
        measuredImage: geometryContextRef.current?.measuredImage,
        requireMeasuredImageIds,
      })
      if (validationError) {
        setError(validationError)
        return false
      }
      currentAreasRef.current = proposed
      setDraft(proposed)
      setError(undefined)
      return true
    },
    [savedAreas]
  )

  const save = useCallback(() => {
    if (inFlightSaveRef.current) return inFlightSaveRef.current
    if (disabled) return Promise.resolve()

    const submittedAreas = currentAreasRef.current
    if (isEqual(submittedAreas, savedAreas)) return Promise.resolve()

    setSaving(true)
    setError(undefined)

    const savePromise: Promise<void> = Promise.resolve()
      .then(async () => {
        const validationError = validateInspectionAreaGeometryChanges({
          baselineAreas: savedAreas,
          proposedAreas: submittedAreas,
          zones: geometryContextRef.current?.zones ?? [],
          measuredImage: geometryContextRef.current?.measuredImage,
        })
        if (validationError) {
          setError(validationError)
          throw new Error(validationError)
        }

        try {
          await persist(submittedAreas)
          setDraft(undefined)
          setError(undefined)
        } catch (cause) {
          setError(
            'Could not save inspection areas. Your changes are still available.'
          )
          throw cause
        }
      })
      .finally(() => {
        setSaving(false)
        if (inFlightSaveRef.current === savePromise)
          inFlightSaveRef.current = undefined
      })
    inFlightSaveRef.current = savePromise
    return savePromise
  }, [disabled, persist, savedAreas])

  const reset = useCallback(() => {
    if (disabled || inFlightSaveRef.current) return
    currentAreasRef.current = savedAreas
    setDraft(undefined)
    setError(undefined)
  }, [disabled, savedAreas])

  const add = useCallback(
    (zone: ZoneParams, drawing?: InspectionAreaDrawing) => {
      if (disabled || inFlightSaveRef.current) return undefined
      const id = v4()
      const proposed = addInspectionArea(
        currentAreasRef.current,
        zone,
        id,
        drawing
      )
      if (drawing && !accept(proposed, [id])) return undefined
      if (!drawing) {
        currentAreasRef.current = proposed
        setDraft(proposed)
        setError(undefined)
      }
      return id
    },
    [accept, disabled]
  )

  const update = useCallback(
    (id: string, update: InspectionAreaUpdate) => {
      if (disabled || inFlightSaveRef.current) return undefined
      const current = currentAreasRef.current
      const previousArea = current.find((area) => area.id === id)
      if (!previousArea) return undefined
      const proposed = updateInspectionArea(current, id, update)
      const nextArea = proposed.find((area) => area.id === id)
      if (!nextArea) return undefined
      if (previousArea.sourceZoneId !== nextArea.sourceZoneId) {
        setError(`${previousArea.title} cannot change its source zone.`)
        return undefined
      }
      if (inspectionAreaGeometryChanged(previousArea, nextArea)) {
        if (!accept(proposed, [id])) return undefined
      } else {
        currentAreasRef.current = proposed
        setDraft(proposed)
        setError(undefined)
      }
      return id
    },
    [accept, disabled]
  )

  const remove = useCallback(
    (id: string) => {
      if (disabled || inFlightSaveRef.current) return
      const proposed = removeInspectionArea(currentAreasRef.current, id)
      currentAreasRef.current = proposed
      setDraft(proposed)
      setError(undefined)
    },
    [disabled]
  )

  return { areas, hasChanges, saving, error, save, reset, add, update, remove }
}
